// @ts-nocheck
import { useMemo, useRef } from "react";
import { useNavigation, useRouteLoaderData, useSearchParams } from "react-router-dom";
import exceljs from "exceljs";
import {saveAs} from "file-saver";
import { useUiStore } from "src/components/main/uiStore";
//-------------------------------------------
import { useAuthStore } from "src/components/main/authStore";
import {
    MyDate,
    mimeTypeMap,
    weeknum,
    getDatefromWeekNum,
} from "src/components/common/functions";
import Chart from "./chart";
//-------------------------------------------
export default function LaborUsage(){
    const rawLoaderData = useRouteLoaderData("LaborUsage");
    const loaderData = useMemo(() => rawLoaderData ?? {}, [rawLoaderData]);
    const navigation = useNavigation();
    const [searchParams, setSearchParams] = useSearchParams();
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const user = useAuthStore(state => state.user);
    const myChart = useRef(null);
    const onComplete = () => {
        const chartInstance = myChart.current?.chart;
        if(!chartInstance) return;
        const ctx = chartInstance.ctx;
        if(!ctx) return;
        ctx.font = Chart.helpers?.fontString(Chart.defaults.global.defaultFontSize, Chart.defaults.global.defaultFontStyle, Chart.defaults.global.defaultFontFamily) ?? ctx.font;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        myChart.current.data.datasets.forEach((dataset, i) => {
            const meta = chartInstance.controller.getDatasetMeta(i);
            meta.data.forEach((bar, index) => {
                const data = dataset.data[index];
                if(data > 0){
                    ctx.fillText(data, bar._model.x, bar._model.y - 5);
                }
            });
        });
    };
    const weeks = useMemo(() => {
        const dateArray = [];
        const min = new Date("2021/03/08");
        const max = new Date();
        while(min < max){
            dateArray.push(weeknum(max));
            max.setDate(max.getDate() - 7);
        }
        return dateArray;
    }, []);
    const active = searchParams.get("mode") || loaderData.mode || "Month";
    const month = searchParams.get("month") || loaderData.month || new MyDate().s().slice(0, 7);
    const week = searchParams.get("week") || loaderData.week || weeknum(new MyDate());
    const department = searchParams.get("department") || loaderData.department || "";
    const showLoading = navigation.state === "loading";
    const points = useMemo(() => Array.isArray(loaderData.points) ? loaderData.points : [], [loaderData]);
    const departmentOptions = useMemo(() => Array.isArray(loaderData.departmentOptions) ? loaderData.departmentOptions : [], [loaderData]);
    const curData = useMemo(() => ({
        xs: points.map(item => item.date),
        ys: points.map(item => Number(item.worked || 0)),
        zs: points.map(item => Number(item.total || 0)),
        us: points.map(item => Number(item.utilization || 0)),
    }), [points]);
    const updateSearch = updates => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if(value === "" || value === undefined || value === null){
                next.delete(key);
            }else{
                next.set(key, String(value));
            }
        });
        setSearchParams(next);
    };
    const exportFunc = () => {
        const workbook = new exceljs.Workbook();
        workbook.creator = user.userName;
        workbook.created = new Date();
        workbook.calcProperties.fullCalcOnLoad = true;
        setShowLoader(true);
        const worksheet = workbook.addWorksheet("Sheet1", {views: [{showGridLines: false}]});
        worksheet.state = "visible";
        worksheet.properties.defaultColWidth = 22.57;
        const rows = [];
        for(let i = 0; i < curData.xs.length; i++){
            rows.push([
                curData.xs[i],
                curData.ys[i],
                curData.zs[i],
                curData.zs[i] ? (curData.ys[i] / curData.zs[i]) : 0
            ]);
        }
        const columns = [
            { name: "Date", filterButton: true, totalsRowLabel: "Average"},
            { name: "Worked", filterButton: true, totalsRowFunction: "average"},
            { name: "Total", filterButton: true, totalsRowFunction: "average"},
            { name: "Utilization", filterButton: true, totalsRowFunction: "average"},
        ];
        worksheet.addTable({
            name: "MyTable",
            ref: "A1",
            totalsRow: true,
            style: {
                showRowStripes: true,
            },
            columns,
            rows,
        });
        worksheet.getColumn(4).eachCell(cell => {
            cell.numFmt = "0.00%";
        });
        worksheet.columns.forEach(column => {
            const minLength = 18;
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, cell => {
                if(cell.row === 1) return;
                const columnLength = cell.value
                    ? cell.value.toString().length * (cell.font ? cell.font.size / 11 * 1.2 : 1.3)
                    : minLength;
                if(columnLength > maxLength){
                    maxLength = columnLength;
                }
            });
            column.width = maxLength < minLength ? minLength : maxLength;
        });
        worksheet.getRows(2, rows.length)?.forEach(row => {
            row.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        });
        workbook.xlsx.writeBuffer().then(buffer => {
            saveAs(new Blob([buffer], { type: mimeTypeMap.xlsx }), `labor usage export ${(new MyDate()).s()}.xlsx`);
            setShowLoader(false);
        });
    };
    return (
        <div className = "my-3 px-3 pb8rem">
            <div className = "d-flex align-items-center mb-3">
                <label className = "mb-0 me-2 alignSelf-center">Mode</label>
                <select
                    required
                    className = "me-3 form-control inline"
                    value = {active}
                    onChange = {e => updateSearch({ mode: e.currentTarget.value })}
                >
                    <option value = "">Select</option>
                    <option>Week</option>
                    <option>Month</option>
                </select>
                {active === "Month" ?
                    <input
                        required
                        className = "me-3 form-control inline"
                        type = "month"
                        value = {month}
                        max = {(new MyDate()).s().slice(0, 7)}
                        onChange = {e => updateSearch({ month: e.currentTarget.value })}
                    />
                    : null}
                {active === "Week" ?
                    <select
                        required
                        className = "me-3 form-control inline"
                        value = {week}
                        onChange = {e => updateSearch({ week: e.currentTarget.value })}
                    >
                        <option value = "">Select</option>
                        {weeks.map((wk, key) => <option key = {key} value = {wk}>{wk}</option>)}
                    </select>
                    : null}

                <label className = "mb-0 me-2 alignSelf-center">Department</label>
                <select
                    required
                    className = "form-control inline"
                    value = {department}
                    onChange = {e => updateSearch({ department: e.currentTarget.value })}
                >
                    <option value = "">Select</option>
                    {departmentOptions.map((option, key) => <option key = {key} value = {option.value}>{option.label}</option>)}
                </select>
                <span className = "ms-3 btn btn-primary" onClick = {exportFunc}>
                    Export
                </span>
            </div>
            {((active === "Month" && month) || (active === "Week" && week)) && department ?
                <div className = "flex-grow-1">
                    <Chart
                        myChart = {myChart}
                        chartConfig = {{
                            type: "line",
                            data: {
                                labels: curData.xs,
                                datasets:  [{
                                    label: "Work",
                                    data: curData.ys,
                                    borderColor: "rgb(75, 192, 192)",
                                    fill: false,
                                    tension: 0,
                                },
                                {
                                    label: "Total",
                                    data: curData.zs,
                                    tension: 0,
                                    borderColor: "rgb(54, 162, 235)",
                                    fill: false,
                                }]
                            },
                            options: {
                                animation:{ onComplete },
                                hover: {
                                    animationDuration: 0
                                },
                                legend: {
                                    display: true
                                },
                                title: {
                                    fontSize: 20,
                                    display: true,
                                    text: `Labor Usage on ${active === "Month" ? month : ""}${active === "Week" ? `${week} (${getDatefromWeekNum(week, 0)} ~ ${getDatefromWeekNum(week, 6)})` : ""}`
                                },
                                scales: {
                                    xAxes: [{
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Date"
                                        }
                                    }],
                                    yAxes: [{
                                        ticks: {
                                            max: Math.ceil(1.1 * Math.max(...curData.zs, 0)),
                                            beginAtZero: true,
                                            callback: value => {if(value % 1 === 0) return value;}
                                        },
                                        scaleLabel: {
                                            fontSize: 15,
                                            display: true,
                                            labelString: "Number of People"
                                        },
                                    }]
                                }
                            }
                        }}
                    />
                </div>
                : null}
            {showLoading ? <div className = "cssLoading">Loading...</div> : null}
        </div>
    );
}
